import { ethereumNetworkInfo } from '@tokenyico/design-system';
import {
  DsBadge,
  DsCard,
  DsExplorerLink,
  DsIcon,
  DsNumber,
  DsTag,
  DsTooltip,
} from '@tokenyico/design-system/dist/reactJS';
import React from 'react';
import { connect } from 'redux-zero/react';
import { useTranslation } from 'react-i18next';
import BigNumber from 'bignumber.js';
import useCopyToClipboard from 'app/helpers/useCopyToClipboard';
import { displayCurrency } from 'app/helpers/currency';

const SubscriptionPaymentInfoView = ({ order, user, subscription }) => {
  const {
    investmentCurrency,
    investmentAmountAtCreation,
    feeAtCreation,
    paymentNetwork,
    holderEmail,
    paymentReference,
    paymentAddress,
  } = order;
  const { t } = useTranslation();
  const [isCopied, copy] = useCopyToClipboard();
  const paymentMethod = subscription.paymentMethods[investmentCurrency];
  const amountToPay = new BigNumber(investmentAmountAtCreation ?? '0')
    .plus(feeAtCreation ?? '0')
    .toString();
  const paymentWalletAddress =
    paymentAddress || paymentMethod.strategy?.unique_address_for_all_investors?.paymentAddress;
  const investorWallet = user.walletsAndStatus[0].wallet;

  return (
    <div className="payment-info">
      <h3>{t('shared.paymentInfo')}</h3>
      <p>{t('shared.paymentInfoDescription', { email: holderEmail })}</p>
      {paymentMethod.type.toLowerCase() === 'crypto' ? (
        <>
          <DsCard shadowDistance="1" padding="3">
            <p>{t('shared.amountToPay')}</p>
            <code>
              <DsNumber number={amountToPay} currency={investmentCurrency} />
            </code>
            <>
              <div className="payment-info__from-to">
                {paymentMethod.strategy?.unique_address_for_all_investors ? (
                  <div>
                    <span className="payment-info__from-to--title">
                      {t('shared.from')}
                      <DsTooltip text={t('shared.yourWallet')}>
                        <DsIcon name="info" color="var(--coolGrey-400)" size="12px" />
                      </DsTooltip>
                    </span>
                    <DsExplorerLink
                      address={investorWallet}
                      network={paymentNetwork}
                      type="wallet"
                    />
                  </div>
                ) : null}

                <div>
                  <span className="payment-info__from-to--title">
                    {t('shared.to')}
                    <DsTooltip text={t('shared.issuersWallet')}>
                      <DsIcon name="info" color="var(--coolGrey-400)" size="12px" />
                    </DsTooltip>
                  </span>
                  <DsExplorerLink
                    address={paymentWalletAddress}
                    network={paymentNetwork}
                    type="wallet"
                  />
                </div>
              </div>
              <div className="payment-info__network">
                <p>{t('shared.paymentNetwork')}</p>
                <span>
                  <DsBadge isDot />{' '}
                  <p>{ethereumNetworkInfo.get(paymentNetwork)?.name || paymentNetwork}</p>
                </span>
              </div>
            </>
          </DsCard>
          <DsTag
            label={t('shared.paymentInfoTag')}
            version="warning"
            icon="warning"
            iconSize="1rem"
            size="md"
            isSquare
          />
          <div className="payment-info__how-to">
            <div className="payment-info__how-to__header">
              <p>{t('shared.howToMakePayment')}</p>
              <DsIcon name="help" color="var(--coolGrey-400)" size="14px" />
            </div>
            <div className="payment-instruction">
              {paymentWalletAddress ? (
                <>
                  <p>
                    {t('paymentInstruction.manual.brief', {
                      paymentNetwork:
                        ethereumNetworkInfo.get(paymentNetwork)?.name || paymentNetwork,
                    })}
                  </p>
                  <ul>
                    {[
                      'bullet1',
                      paymentMethod.strategy?.unique_address_for_all_investors
                        ? 'bullet2v1'
                        : 'bullet2v2',
                      'bullet3',
                      'bullet4',
                    ].map((step) => (
                      <li key={step}>
                        {t(`paymentInstruction.manual.${step}`, {
                          paymentNetwork:
                            ethereumNetworkInfo.get(paymentNetwork)?.name || paymentNetwork,
                          senderWallet: investorWallet.substring(0, 5) + '...',
                          recipientWallet: paymentWalletAddress.substring(0, 5) + '...',
                        })}
                      </li>
                    ))}
                  </ul>
                </>
              ) : (
                <p>{t('shared.paymentAddressNotAvailable')}</p>
              )}
            </div>

            {/* <DsTabs
              onTabChange={({ detail }) => setCurrentTab(detail)}
              currentTab={currentTab}
              version="border"
            >
              <DsTab activeClassName="active-tab">
                <span>{t('shared.manual')}</span>
              </DsTab>

              <DsTab activeClassName="active-tab">
                <span>{t('shared.qrCode')}</span>
              </DsTab>
              <div className="tabs-border-extension" />
              </DsTabs> */}
            {/* {currentTab === 0 ? (
              <div className="payment-instruction">
                <p>{t('paymentInstruction.manual.brief')}</p>
                <ul>
                  {['first', 'second', 'third', 'fourth'].map((step) => (
                    <li key={step}>{t(`paymentInstruction.manual.${step}`)}</li>
                  ))}
                </ul>
              </div>
            ) : (
              <div className="payment-instruction with-qrcode">
                <ul>
                  {['first', 'second', 'third', 'fourth', 'fifth'].map((step) => (
                    <li key={step}>{t(`paymentInstruction.qrCode.${step}`)}</li>
                  ))}
                </ul>
                {paymentMethod.strategy
                  .unique_address_for_all_investors ? (
                  <DsQrcode
                    value={`ethereum:${paymentMethod.strategy.unique_address_for_all_investors.paymentAddress}?value=${feeAtCreation}`}
                    size="100"
                  />
                ) : null}
              </div>
            )} */}
            <DsTag
              label={t('paymentInstruction.infoTag')}
              version="info"
              icon="info"
              iconSize="1rem"
              isSquare
            />
          </div>
        </>
      ) : (
        <>
          <DsCard shadowDistance="1" padding="3">
            <p>{t('shared.amountToPay')}</p>
            <code>{displayCurrency(amountToPay, investmentCurrency)}</code>
            {paymentMethod.type.toLowerCase() === 'fiat' ? (
              <>
                <div className="sub-payment__transfer">
                  {paymentReference ? (
                    <>
                      <p>{t('shared.paymentReference')}</p>
                      <div>
                        <span className="sub-payment__reference">{paymentReference}</span>
                        <DsTooltip text={isCopied ? t('shared.copied') : t('shared.copy')}>
                          <DsIcon
                            className="sub-payment__reference--copy"
                            name="copy"
                            color="var(--blue-400)"
                            size="1rem"
                            onClick={() => copy(paymentReference)}
                          />
                        </DsTooltip>
                      </div>
                    </>
                  ) : null}
                  <div className="sub-payment__bank-transfer _grid">
                    <div className="_col(6)">
                      {paymentMethod.strategy.bankTransfer.bankName && (
                        <div className="sub-payment__bank-transfer--item-key">
                          {t('shared.bankName')}
                        </div>
                      )}
                      {paymentMethod.strategy.bankTransfer.accountOwner && (
                        <div className="sub-payment__bank-transfer--item-key">
                          {t('shared.accountOwner')}
                        </div>
                      )}
                      {paymentMethod.strategy.bankTransfer.BIC && (
                        <div className="sub-payment__bank-transfer--item-key">
                          {t('shared.swiftbic')}
                        </div>
                      )}
                      {paymentMethod.strategy.bankTransfer.IBAN && (
                        <div className="sub-payment__bank-transfer--item-key">
                          {t('shared.iban')}
                        </div>
                      )}
                      {Object.entries(paymentMethod.strategy.bankTransfer).map((entry) => {
                        const [key, value] = entry;
                        const isHTML = /<\/?[a-z][\s\S]*>/i.test(value);
                        if (['bankName', 'accountOwner', 'BIC', 'IBAN'].includes(key) || isHTML)
                          return;
                        const str = key.replace(/([A-Z])/g, ' $1');
                        return (
                          <div key={key} className="sub-payment__bank-transfer--item-key">
                            {str.charAt(0).toUpperCase() + str.slice(1)}
                          </div>
                        );
                      })}
                    </div>

                    <div className="_col(6)">
                      <div className="sub-payment__bank-transfer--item-value">
                        {paymentMethod.strategy.bankTransfer.bankName}
                      </div>
                      <div className="sub-payment__bank-transfer--item-value">
                        {paymentMethod.strategy.bankTransfer.accountOwner}
                      </div>
                      <div className="sub-payment__bank-transfer--item-value">
                        {paymentMethod.strategy.bankTransfer.BIC}
                      </div>
                      <div className="sub-payment__bank-transfer--item-value">
                        {paymentMethod.strategy.bankTransfer.IBAN}
                      </div>
                      {Object.entries(paymentMethod.strategy.bankTransfer).map((entry) => {
                        const [key, value] = entry;
                        const isHTML = /<\/?[a-z][\s\S]*>/i.test(value);
                        if (['bankName', 'accountOwner', 'BIC', 'IBAN'].includes(key) || isHTML)
                          return;
                        return (
                          <div key={key} className="sub-payment__bank-transfer--item-value">
                            {value}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </>
            ) : null}
          </DsCard>
          <DsTag
            label={t('shared.paymentReferenceTag')}
            version="warning"
            icon="warning"
            iconSize="1rem"
            isSquare
          />
        </>
      )}
    </div>
  );
};

export default connect((state) => state.holder)(SubscriptionPaymentInfoView);
